import './contentModalSupportInfo.css';

import { useTranslation } from 'react-i18next';

import DemoTag from '@/components/DemoTag/DemoTag';
import { TrendingValue } from '@/components/TrendingValue/TrendingValue';
import { useDemoContext } from '@/hooks/DemoContext';
import { TRACKING_INSTANCE } from '@/hooks/useTracking/useTrackingInit/useTrackingInit';
import { Support } from '@/services/types/commonResponse';

interface ContentModalSupportInfoProps {
  support: Support;
  copyCode: (isin: string | undefined) => void;
  isCopied: boolean;
  dateOuverture: string;
}

export const ContentModalSupportInfo = ({
  support,
  copyCode,
  isCopied,
  dateOuverture,
}: ContentModalSupportInfoProps) => {
  const { t } = useTranslation();
  const { isDemo } = useDemoContext();

  const getModeGestionText = () => {
    if (support?.modeGestionLibelle === 'Pilotée A Horizon') {
      return 'Gestion Pilotée à Horizon Retraite';
    } else {
      return `Gestion ${support?.modeGestionLibelle}`;
    }
  };

  return (
    <div className="modal-support-infos">
      <div className="modal-support-top-modal">
        <span>{support?.nomSupport} </span>
        <span className="modal-support-isin-code">
          {support?.isin}{' '}
          <button
            type="button"
            aria-label={t('supports.copyIsinLabel')}
            onClick={() => {
              copyCode(support?.isin);
              void TRACKING_INSTANCE.triggerEvent('clic', undefined, {
                event_id: 'clic',
                event_categorie: 'clic',
                event_metier: 'espace client placement',
                event_nom: 'copier code support',
                event_detail_1: 'picto',
              });
            }}
          >
            <i className="totem-icon totem-icon-copy" aria-hidden="true" />
          </button>
          {isCopied && <span className="modal-support-badge-isin-copied">{t('supports.isinCopied')}</span>}{' '}
        </span>
        {support?.modeGestion && <span className="mode-gestion">{getModeGestionText()}</span>}
      </div>
      <div className="modal-support-montant-performance">
        {isDemo && <DemoTag />}
        <div className="modal-support-montant-pourcentage">
          <span className="modal-support-montant">
            {support?.allocation === null
              ? t('common.infoUnavailable')
              : t('format.currencyFlexible', { value: support?.allocation })}{' '}
          </span>
          <span className="modal-pourcentage-support">
            {support?.poids === null ? (
              `(${t('common.infoUnavailable')})`
            ) : (
              <>({t('format.percentFlexible', { value: support.poids })})</>
            )}
          </span>{' '}
        </div>
        {typeof support?.pmvEuro === 'number' && (
          <div className="modal-support-performance">
            <TrendingValue value={support.pmvEuro} secondaryValue={support?.pmvPct || 0} />
          </div>
        )}
      </div>
      <hr className="modal-support-hr" />
      <div className="modal-support-infos-supp">
        <div className="modal-support-prix-actuel">
          <span className="modal-support-prix-actuel-title">Quantité</span>
          {typeof support?.nbParts === 'number' ? (
            <span className="modal-support-prix-actuel-value">
              {t('format.numberSignificant', { value: support?.nbParts })}
            </span>
          ) : (
            <span className="info-indisponible">{t('common.infoUnavailable')}</span>
          )}
        </div>
        <div className="modal-support-prix-actuel">
          <span className="modal-support-prix-actuel-title">Prix revient unit.</span>
          {typeof support?.prm === 'number' ? (
            <span className="modal-support-prix-actuel-value">
              {t('format.currencyFlexible', { value: support?.prm })}
            </span>
          ) : (
            <span className="info-indisponible">{t('common.infoUnavailable')}</span>
          )}
        </div>
        <div className="modal-support-prix-actuel">
          <span className="modal-support-prix-actuel-title">Prix actuel</span>
          {typeof support?.cotation === 'number' ? (
            <span className="modal-support-prix-actuel-value">
              {t('format.currencyFlexible', { value: support?.cotation })} au {dateOuverture}
            </span>
          ) : (
            <span className="info-indisponible">{t('common.infoUnavailable')}</span>
          )}
        </div>
      </div>
    </div>
  );
};
